/**
 * Performance monitoring utility for tracking request timing and metrics
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.requestTimings = new Map();
    this.loadTimeThresholds = {
      fast: 1000,      // < 1s
      normal: 3000,    // 1-3s
      slow: 5000,      // 3-5s
      critical: 10000  // > 10s
    };
    
    // Performance metrics storage
    this.performanceData = {
      requests: [],
      loadTimes: [],
      errors: [],
      cacheHits: 0,
      cacheMisses: 0
    };
    
    // Initialize performance observer if available
    this.initializePerformanceObserver();
  }

  /**
   * Initialize Performance Observer for navigation and resource timing
   */
  initializePerformanceObserver() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        // Observe navigation timing
        const navObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.recordNavigationTiming(entry);
          }
        });
        navObserver.observe({ entryTypes: ['navigation'] });

        // Observe resource timing
        const resourceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name.includes('/api/')) {
              this.recordResourceTiming(entry);
            }
          }
        });
        resourceObserver.observe({ entryTypes: ['resource'] });

      } catch (error) {
        console.warn('Performance Observer not fully supported:', error);
      }
    }
  }

  /**
   * Start timing a request
   * @param {string} requestId - Unique request identifier
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {object} metadata - Additional request metadata
   */
  startTiming(requestId, endpoint, method = 'GET', metadata = {}) {
    const startTime = performance.now();
    
    this.requestTimings.set(requestId, {
      requestId,
      endpoint,
      method,
      startTime,
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        connectionType: this.getConnectionType(),
        timestamp: new Date().toISOString()
      }
    });

    // Started timing request
  }

  /**
   * End timing a request and record metrics
   * @param {string} requestId - Request identifier
   * @param {object} result - Request result information
   */
  endTiming(requestId, result = {}) {
    const timing = this.requestTimings.get(requestId);
    if (!timing) {
      console.warn(`[PerformanceMonitor] No timing found for request: ${requestId}`);
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - timing.startTime;
    
    const metrics = {
      ...timing,
      endTime,
      duration,
      success: result.success !== false,
      status: result.status,
      error: result.error,
      cacheHit: result.cacheHit || false,
      retryCount: result.retryCount || 0,
      performanceCategory: this.categorizePerformance(duration)
    };

    // Store metrics
    this.performanceData.requests.push(metrics);
    this.requestTimings.delete(requestId);

    // Update cache statistics
    if (result.cacheHit) {
      this.performanceData.cacheHits++;
    } else {
      this.performanceData.cacheMisses++;
    }

    // Request completed

    // Log slow requests
    if (duration > this.loadTimeThresholds.slow) {
      console.warn(`[PerformanceMonitor] Slow request detected: ${timing.endpoint} took ${duration.toFixed(2)}ms`);
    }

    return metrics;
  }

  /**
   * Record load time for a specific operation with enhanced initialization tracking
   * @param {string} operation - Operation name (e.g., 'wizard_initialization', 'parrot_load')
   * @param {number} duration - Duration in milliseconds
   * @param {object} metadata - Additional metadata
   */
  recordLoadTime(operation, duration, metadata = {}) {
    const loadTimeData = {
      operation,
      duration,
      timestamp: new Date().toISOString(),
      performanceCategory: this.categorizePerformance(duration),
      metadata: {
        ...metadata,
        url: window.location.href,
        userAgent: navigator.userAgent,
        connectionType: this.getConnectionType(),
        memoryUsage: this.getMemoryUsage(),
        isInitializationOperation: operation.includes('initialization') || operation.includes('wizard')
      }
    };

    // Enhanced tracking for initialization operations
    if (loadTimeData.metadata.isInitializationOperation) {
      loadTimeData.initializationMetrics = {
        parrotId: metadata.parrotId,
        dataSource: metadata.dataSource,
        cacheHit: metadata.cacheHit || false,
        retryCount: metadata.retryCount || 0,
        stepCount: metadata.stepCount || 0,
        errorCount: metadata.errorCount || 0,
        networkCondition: this.getNetworkCondition()
      };

      // Track initialization performance trends
      this.trackInitializationTrend(operation, duration, loadTimeData.initializationMetrics);
    }

    this.performanceData.loadTimes.push(loadTimeData);

    // Enhanced logging for initialization operations
    if (loadTimeData.metadata.isInitializationOperation) {
      const level = duration > this.loadTimeThresholds.slow ? 'warn' : 'log';
      console[level](`[PerformanceMonitor] Initialization: ${operation} - ${duration.toFixed(2)}ms (${loadTimeData.performanceCategory})`, {
        parrotId: metadata.parrotId,
        dataSource: metadata.dataSource,
        cacheHit: metadata.cacheHit,
        retryCount: metadata.retryCount
      });
    } else if (duration > this.loadTimeThresholds.normal) {
      // Load time recorded
    }

    // Alert for critical performance issues
    if (duration > this.loadTimeThresholds.critical) {
      console.error(`[PerformanceMonitor] CRITICAL: ${operation} took ${duration.toFixed(2)}ms - investigating performance issue`);
      this.reportPerformanceIssue(operation, duration, this.loadTimeThresholds.critical, metadata);
    }

    return loadTimeData;
  }

  /**
   * Get current memory usage information
   */
  getMemoryUsage() {
    if (performance.memory) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        usagePercentage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
      };
    }
    return null;
  }

  /**
   * Get network condition information
   */
  getNetworkCondition() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      online: navigator.onLine,
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || null,
      rtt: connection?.rtt || null,
      saveData: connection?.saveData || false
    };
  }

  /**
   * Track initialization performance trends
   */
  trackInitializationTrend(operation, duration, metrics) {
    if (!this.initializationTrends) {
      this.initializationTrends = new Map();
    }

    const key = `${operation}-${metrics.dataSource || 'unknown'}`;
    if (!this.initializationTrends.has(key)) {
      this.initializationTrends.set(key, {
        operation,
        dataSource: metrics.dataSource,
        samples: [],
        averageDuration: 0,
        minDuration: Infinity,
        maxDuration: 0,
        trendDirection: 'stable'
      });
    }

    const trend = this.initializationTrends.get(key);
    trend.samples.push({
      duration,
      timestamp: Date.now(),
      cacheHit: metrics.cacheHit,
      retryCount: metrics.retryCount,
      networkCondition: metrics.networkCondition?.effectiveType
    });

    // Keep only last 20 samples
    if (trend.samples.length > 20) {
      trend.samples = trend.samples.slice(-20);
    }

    // Update statistics
    const durations = trend.samples.map(s => s.duration);
    trend.averageDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
    trend.minDuration = Math.min(...durations);
    trend.maxDuration = Math.max(...durations);

    // Calculate trend direction
    if (trend.samples.length >= 5) {
      const recent = trend.samples.slice(-5).map(s => s.duration);
      const older = trend.samples.slice(-10, -5).map(s => s.duration);
      
      if (older.length > 0) {
        const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
        const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
        
        if (recentAvg > olderAvg * 1.2) {
          trend.trendDirection = 'degrading';
        } else if (recentAvg < olderAvg * 0.8) {
          trend.trendDirection = 'improving';
        } else {
          trend.trendDirection = 'stable';
        }
      }
    }
  }

  /**
   * Report performance issue to error reporting system
   */
  reportPerformanceIssue(operation, duration, threshold, metadata) {
    // Import error reporting dynamically to avoid circular dependencies
    import('./consolidatedErrorHandling.js').then(({ errorReporting }) => {
      errorReporting.reportError(new Error(`Performance issue: ${operation}`), {
        ...metadata,
        performanceCategory: this.categorizePerformance(duration),
        memoryUsage: this.getMemoryUsage(),
        networkCondition: this.getNetworkCondition()
      });
    }).catch(error => {
      console.warn('[PerformanceMonitor] Failed to report performance issue:', error);
    });
  }

  /**
   * Record an error with timing information
   * @param {string} requestId - Request identifier
   * @param {Error} error - Error object
   * @param {object} context - Additional context
   */
  recordError(requestId, error, context = {}) {
    const timing = this.requestTimings.get(requestId);
    const duration = timing ? performance.now() - timing.startTime : null;

    const errorData = {
      requestId,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        status: error.status
      },
      duration,
      timestamp: new Date().toISOString(),
      context: {
        ...context,
        endpoint: timing?.endpoint,
        method: timing?.method,
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };

    this.performanceData.errors.push(errorData);

    // Clean up timing if exists
    if (timing) {
      this.requestTimings.delete(requestId);
    }

    console.error(`[PerformanceMonitor] Error recorded for request: ${requestId}`, errorData);
    return errorData;
  }

  /**
   * Record navigation timing from Performance Observer
   * @param {PerformanceNavigationTiming} entry - Navigation timing entry
   */
  recordNavigationTiming(entry) {
    const navigationMetrics = {
      type: 'navigation',
      name: entry.name,
      duration: entry.duration,
      domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
      loadComplete: entry.loadEventEnd - entry.loadEventStart,
      dnsLookup: entry.domainLookupEnd - entry.domainLookupStart,
      tcpConnect: entry.connectEnd - entry.connectStart,
      serverResponse: entry.responseEnd - entry.responseStart,
      domProcessing: entry.domComplete - entry.domLoading,
      timestamp: new Date().toISOString()
    };

    this.performanceData.loadTimes.push(navigationMetrics);
    // Navigation timing recorded
  }

  /**
   * Record resource timing from Performance Observer
   * @param {PerformanceResourceTiming} entry - Resource timing entry
   */
  recordResourceTiming(entry) {
    const resourceMetrics = {
      type: 'resource',
      name: entry.name,
      duration: entry.duration,
      transferSize: entry.transferSize,
      encodedBodySize: entry.encodedBodySize,
      decodedBodySize: entry.decodedBodySize,
      dnsLookup: entry.domainLookupEnd - entry.domainLookupStart,
      tcpConnect: entry.connectEnd - entry.connectStart,
      serverResponse: entry.responseEnd - entry.responseStart,
      timestamp: new Date().toISOString(),
      performanceCategory: this.categorizePerformance(entry.duration)
    };

    this.performanceData.requests.push(resourceMetrics);
    
    if (entry.duration > this.loadTimeThresholds.slow) {
      console.warn('[PerformanceMonitor] Slow API resource detected:', resourceMetrics);
    }
  }

  /**
   * Categorize performance based on duration
   * @param {number} duration - Duration in milliseconds
   * @returns {string} Performance category
   */
  categorizePerformance(duration) {
    if (duration < this.loadTimeThresholds.fast) return 'fast';
    if (duration < this.loadTimeThresholds.normal) return 'normal';
    if (duration < this.loadTimeThresholds.slow) return 'slow';
    if (duration < this.loadTimeThresholds.critical) return 'critical';
    return 'timeout';
  }

  /**
   * Get connection type information
   * @returns {string} Connection type
   */
  getConnectionType() {
    if (typeof navigator !== 'undefined' && navigator.connection) {
      return navigator.connection.effectiveType || navigator.connection.type || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Get performance summary statistics
   * @returns {object} Performance summary
   */
  getPerformanceSummary() {
    const requests = this.performanceData.requests.filter(r => r.duration);
    const loadTimes = this.performanceData.loadTimes.filter(l => l.duration);
    
    if (requests.length === 0 && loadTimes.length === 0) {
      return {
        totalRequests: 0,
        averageResponseTime: 0,
        cacheHitRate: 0,
        errorRate: 0,
        performanceDistribution: {}
      };
    }

    const allDurations = [
      ...requests.map(r => r.duration),
      ...loadTimes.map(l => l.duration)
    ];

    const performanceDistribution = {
      fast: requests.filter(r => r.performanceCategory === 'fast').length,
      normal: requests.filter(r => r.performanceCategory === 'normal').length,
      slow: requests.filter(r => r.performanceCategory === 'slow').length,
      critical: requests.filter(r => r.performanceCategory === 'critical').length,
      timeout: requests.filter(r => r.performanceCategory === 'timeout').length
    };

    return {
      totalRequests: requests.length,
      totalLoadOperations: loadTimes.length,
      averageResponseTime: allDurations.reduce((a, b) => a + b, 0) / allDurations.length,
      medianResponseTime: this.calculateMedian(allDurations),
      p95ResponseTime: this.calculatePercentile(allDurations, 95),
      cacheHitRate: this.performanceData.cacheHits / (this.performanceData.cacheHits + this.performanceData.cacheMisses) * 100,
      errorRate: this.performanceData.errors.length / Math.max(requests.length, 1) * 100,
      performanceDistribution,
      slowRequestsCount: requests.filter(r => r.duration > this.loadTimeThresholds.slow).length,
      connectionType: this.getConnectionType()
    };
  }

  /**
   * Calculate median of an array
   * @param {number[]} values - Array of numbers
   * @returns {number} Median value
   */
  calculateMedian(values) {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  }

  /**
   * Calculate percentile of an array
   * @param {number[]} values - Array of numbers
   * @param {number} percentile - Percentile to calculate (0-100)
   * @returns {number} Percentile value
   */
  calculatePercentile(values, percentile) {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  }

  /**
   * Get detailed performance data for analysis
   * @param {object} filters - Filters to apply
   * @returns {object} Filtered performance data
   */
  getDetailedMetrics(filters = {}) {
    let filteredRequests = this.performanceData.requests;
    let filteredLoadTimes = this.performanceData.loadTimes;
    let filteredErrors = this.performanceData.errors;

    // Apply time range filter
    if (filters.timeRange) {
      const cutoff = new Date(Date.now() - filters.timeRange).toISOString();
      filteredRequests = filteredRequests.filter(r => r.timestamp > cutoff);
      filteredLoadTimes = filteredLoadTimes.filter(l => l.timestamp > cutoff);
      filteredErrors = filteredErrors.filter(e => e.timestamp > cutoff);
    }

    // Apply endpoint filter
    if (filters.endpoint) {
      filteredRequests = filteredRequests.filter(r => 
        r.endpoint && r.endpoint.includes(filters.endpoint)
      );
    }

    // Apply performance category filter
    if (filters.performanceCategory) {
      filteredRequests = filteredRequests.filter(r => 
        r.performanceCategory === filters.performanceCategory
      );
    }

    return {
      requests: filteredRequests,
      loadTimes: filteredLoadTimes,
      errors: filteredErrors,
      summary: this.calculateSummaryForData(filteredRequests, filteredLoadTimes, filteredErrors)
    };
  }

  /**
   * Calculate summary for filtered data
   * @param {Array} requests - Filtered requests
   * @param {Array} loadTimes - Filtered load times
   * @param {Array} errors - Filtered errors
   * @returns {object} Summary statistics
   */
  calculateSummaryForData(requests, loadTimes, errors) {
    const allDurations = [
      ...requests.map(r => r.duration).filter(d => d),
      ...loadTimes.map(l => l.duration).filter(d => d)
    ];

    if (allDurations.length === 0) {
      return {
        count: 0,
        averageTime: 0,
        errorRate: 0
      };
    }

    return {
      count: requests.length + loadTimes.length,
      averageTime: allDurations.reduce((a, b) => a + b, 0) / allDurations.length,
      medianTime: this.calculateMedian(allDurations),
      errorRate: errors.length / Math.max(requests.length, 1) * 100,
      slowRequestsCount: requests.filter(r => r.duration > this.loadTimeThresholds.slow).length
    };
  }

  /**
   * Clear old performance data to prevent memory leaks
   * @param {number} maxAge - Maximum age in milliseconds (default: 1 hour)
   */
  cleanup(maxAge = 3600000) {
    const cutoff = new Date(Date.now() - maxAge).toISOString();
    
    this.performanceData.requests = this.performanceData.requests.filter(r => r.timestamp > cutoff);
    this.performanceData.loadTimes = this.performanceData.loadTimes.filter(l => l.timestamp > cutoff);
    this.performanceData.errors = this.performanceData.errors.filter(e => e.timestamp > cutoff);

    // Clear any stale request timings
    for (const [requestId, timing] of this.requestTimings.entries()) {
      if (Date.now() - timing.startTime > maxAge) {
        this.requestTimings.delete(requestId);
      }
    }

    // Cleaned up old performance data
  }

  /**
   * Get initialization performance trends
   * @returns {object} Initialization trends data
   */
  getInitializationTrends() {
    if (!this.initializationTrends) {
      return {
        trends: [],
        summary: {
          totalOperations: 0,
          averagePerformance: 'unknown',
          degradingTrends: 0,
          improvingTrends: 0
        }
      };
    }

    const trends = Array.from(this.initializationTrends.values());
    const summary = {
      totalOperations: trends.length,
      averagePerformance: this.calculateOverallPerformance(trends),
      degradingTrends: trends.filter(t => t.trendDirection === 'degrading').length,
      improvingTrends: trends.filter(t => t.trendDirection === 'improving').length,
      stableTrends: trends.filter(t => t.trendDirection === 'stable').length
    };

    return {
      trends: trends.map(trend => ({
        operation: trend.operation,
        dataSource: trend.dataSource,
        averageDuration: Math.round(trend.averageDuration),
        minDuration: Math.round(trend.minDuration),
        maxDuration: Math.round(trend.maxDuration),
        sampleCount: trend.samples.length,
        trendDirection: trend.trendDirection,
        performanceCategory: this.categorizePerformance(trend.averageDuration),
        lastSample: trend.samples[trend.samples.length - 1]
      })),
      summary
    };
  }

  /**
   * Calculate overall performance rating
   */
  calculateOverallPerformance(trends) {
    if (trends.length === 0) return 'unknown';
    
    const avgDurations = trends.map(t => t.averageDuration);
    const overallAvg = avgDurations.reduce((a, b) => a + b, 0) / avgDurations.length;
    
    return this.categorizePerformance(overallAvg);
  }

  /**
   * Export performance data for analysis with enhanced initialization data
   * @returns {object} Exportable performance data
   */
  exportData() {
    return {
      summary: this.getPerformanceSummary(),
      requests: this.performanceData.requests,
      loadTimes: this.performanceData.loadTimes,
      errors: this.performanceData.errors,
      initializationTrends: this.getInitializationTrends(),
      exportedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connectionType: this.getConnectionType(),
      memoryUsage: this.getMemoryUsage(),
      networkCondition: this.getNetworkCondition()
    };
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Auto-cleanup every 30 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    performanceMonitor.cleanup();
  }, 30 * 60 * 1000);
}

export default performanceMonitor;
